import { QueryClient, UseMutateAsyncFunction, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router"
import { SELECTED_WORKSPACE_KEY, USER_DETAILS_KEY } from "../../../constants"
import { StorageService } from "../../../services"
import { UserProfile, Workspace } from "../../../types"
import { clearWorkspaceScopedQueryData } from "../../../utils/queryInvalidation"
import { SetUserWorkspaceResponse, useWorkspaceService } from "../services/useWorkspaceService"
import { TriggerToast } from "../../globals/Toaster"
import { appRoutes } from "../../../routes/appRoutes"

type SelectWorkspaceProps = {
  workspaceId: string
  workspace?: Workspace
}

type SelectWorkspaceContext = {
  previousProfile: UserProfile | null
  previousWorkspace: Workspace | null
  previousWorkspaceId: string | null
}

type UseSelectWorkspaceType = UseMutateAsyncFunction<SetUserWorkspaceResponse, unknown, SelectWorkspaceProps, SelectWorkspaceContext>

const setSelectedWorkspaceCache = (
  queryClient: QueryClient,
  workspaceId: string,
  optimisticWorkspace?: Workspace,
): void => {
  const profile = queryClient.getQueryData<UserProfile>([USER_DETAILS_KEY])
  const selectedWorkspace = optimisticWorkspace ?? profile?.workspaces.find(workspace => workspace?.id === workspaceId)

  if (profile && selectedWorkspace) {
    const workspaceExists = profile.workspaces.some(workspace => workspace?.id === selectedWorkspace.id)
    const workspaces = workspaceExists
      ? profile.workspaces.map(workspace => workspace?.id === selectedWorkspace.id ? selectedWorkspace : workspace)
      : [selectedWorkspace, ...profile.workspaces]

    queryClient.setQueryData([SELECTED_WORKSPACE_KEY], selectedWorkspace)
    queryClient.setQueryData<UserProfile>([USER_DETAILS_KEY], {
      ...profile,
      workspaces,
      active_workspace: [selectedWorkspace],
    })
  }
}

const useSelectWorkspace = (): UseSelectWorkspaceType => {

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const workspaceService = useWorkspaceService()

  const { mutateAsync: selectWorkspaceMutation } = useMutation<SetUserWorkspaceResponse, unknown, SelectWorkspaceProps, SelectWorkspaceContext>({
    onMutate: async (params: SelectWorkspaceProps) => {
      const previousWorkspaceId = await StorageService.getStorageItem<string>(SELECTED_WORKSPACE_KEY)
      const previousWorkspace = queryClient.getQueryData<Workspace>([SELECTED_WORKSPACE_KEY]) ?? null
      const previousProfile = queryClient.getQueryData<UserProfile>([USER_DETAILS_KEY]) ?? null

      await clearWorkspaceScopedQueryData(queryClient)
      await StorageService.setStorageItem(SELECTED_WORKSPACE_KEY, params.workspaceId)
      setSelectedWorkspaceCache(queryClient, params.workspaceId, params.workspace)

      return {
        previousProfile,
        previousWorkspace,
        previousWorkspaceId,
      }
    },
    mutationFn: async (params: SelectWorkspaceProps) => {
      return workspaceService.setUserWorkspace(params.workspaceId)
    },
    onSuccess: async (response: SetUserWorkspaceResponse, params: SelectWorkspaceProps) => {
      const workspaceId = response.workspace_id || params.workspaceId
      await StorageService.setStorageItem(SELECTED_WORKSPACE_KEY, workspaceId)
      setSelectedWorkspaceCache(queryClient, workspaceId, params.workspace?.id === workspaceId ? params.workspace : undefined)

      navigate(appRoutes.experiments.root, { replace: true })
      void queryClient.invalidateQueries()
    },
    onError: async (_error, _params, context) => {
      if (context?.previousWorkspaceId) {
        await StorageService.setStorageItem(SELECTED_WORKSPACE_KEY, context.previousWorkspaceId)
      } else {
        await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY)
      }

      queryClient.setQueryData([SELECTED_WORKSPACE_KEY], context?.previousWorkspace ?? null)
      if (context?.previousProfile) {
        queryClient.setQueryData<UserProfile>([USER_DETAILS_KEY], context.previousProfile)
      }

      TriggerToast("Failed to set selected workspace.", undefined, 'error')
    }
  })

  return selectWorkspaceMutation
}

export default useSelectWorkspace
